import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Inject,
  InjectionToken,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { Dropdown } from 'primeng/dropdown';
import { InputText } from 'primeng/inputtext';
import { AutoLog } from '../../../common/public-api';
import { FORM_VALIDATION_ERROR_MSG_MAP } from '../../../core/constants/message.constant';
import { AppEnvService } from '../../../core/services';
import { isNullOrEmpty } from '../../../core/utils/common-functions';
import * as NGUtils from '../../../core/utils/ng-functions';
import { getFormGroupEx } from '../../../core/utils/validator-functions';
import { BaseComponent } from '../../../shared/components/base.component';
import {
  formatOptions,
  formatOptionsForLocale,
} from '../../pipes/options-mapping.pipe';
import { DropdownOptionsService } from './../../../core/services/dropdown-options.service';
import { CustomValidators } from './../../../core/utils/validator-functions';

export const ADDRESS_OTHER_VALUE = new InjectionToken<string>(
  'Address other value.',
  {
    providedIn: 'root',
    factory: () => 'OTHER',
  }
);

const ADDRESS_LEVEL_TYPE = {
  LEVEL1: 'AddressLevel1',
  LEVEL2: 'AddressLevel2',
  LEVEL3: 'AddressLevel3',
  LEVEL4: 'AddressLevel4',
  LEVEL5: 'Address',
  POSTCODE: 'AddressPostcode',
  POSTCODE_LEVEL: 'AddressPostcodeLevel',
  NATIVE: 'AddressForNative',
};

export enum AddressLevelEnum {
  level1 = 1,
  level2,
  level3,
  level4,
  level5,
  postcode,
  native,
}

export const ADDRESS_SELECTOR_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AddressSelectorComponent),
  multi: true,
};

/**
 * 地址下拉元件
 *
 * @export
 * @class AddressSelectorComponent
 * @extends {BaseComponent}
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {ControlValueAccessor}
 * @example ngModel 使用方式
 * <sigv-address-selector
 *  label="Address Model"
 *  [(ngModel)]="addressObj"
 *  (ngModelChange)="onAddressModelChange($event)">
 * </sigv-address-selector>
 * @example formgroup 使用方式(設定必填)
 * <sigv-address-selector
 *  label="Address Form"
 *  formControlName="address"
 *  [isRequired]="true"
 *  [isNativeAddressRequired]="true"
 *  (valueChange)="onFormAddressChange($event)">
 * </sigv-address-selector>
 * @example 唯讀
 * <sigv-address-selector
 *  label="Address Model"
 *  [(ngModel)]="addressObj"
 *  [readonly]="true">
 * </sigv-address-selector>
 */
@Component({
  selector: 'sigv-address-selector',
  templateUrl: './address-selector.component.html',
  styleUrls: ['./address-selector.component.scss'],
  providers: [ADDRESS_SELECTOR_VALUE_ACCESSOR],
})
export class AddressSelectorComponent
  extends BaseComponent
  implements OnChanges, OnInit, DoCheck, AfterViewInit, ControlValueAccessor
{
  private defaultRequiredValidatorFn: ValidatorFn | null;
  private defaultNativeAddrRequiredValidatorFn: ValidatorFn | null;
  private _control: AbstractControl | null = null;
  private triggeredByUI = false;
  private doCheckLock = false;
  enablePostcode = false;
  enableAddressForNativeLanguage = false;
  readonly validationMsgMap = FORM_VALIDATION_ERROR_MSG_MAP;

  /**
   * 版面配置(每列幾欄)
   */
  @Input() layout = 4;

  /**
   * 地址下拉最大層級
   * 註: 預設讀取 env.json 的 maxAddressLevel 設定值
   */
  @Input() maxAddressLevel?: number;

  /**
   * 是否唯讀
   */
  @Input() readonly = false;

  /**
   * 空值顯示
   */
  @Input() replaceNullWith = '-';

  /**
   * 地址字串顯示格式
   * 註: 字串內需帶入地址參數。
   * 例: "${Address}, ${AddressPostcode} ${AddressLevel2}, ${AddressLevel1}"
   * 預設讀取 env.json 的 printedAddressFormat 設定值
   */
  @Input() printedAddressFormat = '';

  /**
   * Level5 (詳細地址) 最大長度 - 預設2100字數
   */
  @Input() level5MaxLength = 2100;

  /**
   * 是否為必填
   */
  @Input() isRequired = false;

  /**
   * 當地語言地址欄位是否必填
   */
  @Input() isNativeAddressRequired = false;

  /**
   * 必填檢核時機點
   */
  @Input() requiredOn: 'change' | 'submit' = 'change';

  /**
   * 地址欄位的名稱
   */
  @Input() label = '';

  /**
   * 是否顯示native language input
   */
  @Input() disableNativeInput = false;

  /**
   * 消金公版使用
   */
  @Input() addressControl: AbstractControl | any;

  /**
   * 地址資料變更事件
   */
  @Output() valueChange = new EventEmitter<{
    level: AddressLevelEnum;
    value?: string;
  }>();

  @ViewChild('level1DDL') level1DDL?: Dropdown;
  @ViewChild('level2DDL') level2DDL?: Dropdown;
  @ViewChild('level3DDL') level3DDL?: Dropdown;
  @ViewChild('level4DDL') level4DDL?: Dropdown;
  @ViewChild('level5Input') level5Input?: InputText;
  @ViewChild('postcodeDDL') postcodeDDL?: Dropdown;
  @ViewChild('nativeInput') nativeInput?: InputText;

  /**
   * Level 1 下拉選單
   */
  level1Options: SelectItem[] = [];

  /**
   * Level 2 下拉選單
   */
  level2Options: SelectItem[] = [];

  /**
   * Level 3 下拉選單
   */
  level3Options: SelectItem[] = [];

  /**
   * Level 4 下拉選單
   */
  level4Options: SelectItem[] = [];

  /**
   * Post code 下拉選單
   */
  postcodeOptions: SelectItem[] = [];
  displayLevel1Options: SelectItem[] = [];
  displayLevel2Options: SelectItem[] = [];
  displayLevel3Options: SelectItem[] = [];
  displayLevel4Options: SelectItem[] = [];
  postcodeLevel?: number;
  model: AddressViewModel = {};
  pageData = {
    useModel: {} as AddressViewModel,
  };
  previousAddressModelJson = JSON.stringify({});
  globalEnableAddressForNative = false;
  disablePostcodeDDLFunc = () => true;
  onModelChange = (event: AddressViewModel) => {};
  onModelTouched = () => {};

  constructor(
    private injector: Injector,
    @Inject(ADDRESS_OTHER_VALUE) private otherValueCode: string,
    private dropdownOptionsService: DropdownOptionsService,
    private cd: ChangeDetectorRef
  ) {
    super(injector);

    this.enablePostcode =
      this.envConfig?.format?.address?.enablePostcode ?? false;
    this.enableAddressForNativeLanguage =
      this.envConfig?.format?.address?.enableAddressForNativeLanguage ?? false;
    this.globalEnableAddressForNative =
      this.envConfig?.format?.address?.enableAddressForNativeLanguage ?? false;

    this.defaultRequiredValidatorFn = CustomValidators.enableValidatorWhen(
      this.requiredOn === 'submit'
        ? AddressValidators.addressRequiredOnSubmit(this.maxAddressLevel, this)
        : AddressValidators.addressRequired(
            this.maxAddressLevel,
            this,
            this.pageData
          ),
      () => {
        // 唯讀不需要檢核
        if (this.readonly) {
          return false;
        }

        if (this.isRequired) {
          return true;
        }
        const availableLevels = [
          ...[
            this.pageData.useModel.level1,
            this.pageData.useModel.level2,
            this.pageData.useModel.level3,
            this.pageData.useModel.level4,
          ].slice(0, this.maxAddressLevel),
          this.pageData.useModel.level5,
        ];
        const isRequired = availableLevels.some(
          (x) => isNullOrEmpty(x) === false
        );

        return isRequired;
      }
    );

    this.defaultNativeAddrRequiredValidatorFn =
      CustomValidators.enableValidatorWhen(
        this.requiredOn === 'submit'
          ? AddressValidators.nativeAddressRequiredOnSubmit()
          : AddressValidators.nativeAddressRequired(),
        () => {
          // 唯讀不需要檢核
          if (this.readonly) {
            return false;
          }

          return this.isNativeAddressRequired;
        }
      );
  }

  get control(): AbstractControl | null {
    const ngControlDirective = this.injector.get(NgControl);
    const control =
      this.addressControl ?? (ngControlDirective.control as any | null);
    if (control) {
      const toBeAddValidators: ValidatorFn[] = [];
      const validators: ValidatorFn[] = Array.isArray(control._rawValidators)
        ? control._rawValidators
        : !control._rawValidators
        ? []
        : [control._rawValidators];
      if (validators.indexOf(this.defaultRequiredValidatorFn!) < 0) {
        toBeAddValidators.push(this.defaultRequiredValidatorFn!);
      }

      if (this.enableAddressForNativeLanguage) {
        if (
          validators.indexOf(this.defaultNativeAddrRequiredValidatorFn!) < 0
        ) {
          toBeAddValidators.push(this.defaultNativeAddrRequiredValidatorFn!);
        }
      }

      if (toBeAddValidators.length > 0) {
        control.setValidators([...validators, ...toBeAddValidators]);
        control.updateValueAndValidity();
      }

      this._control = control;
    }

    return this._control;
  }

  get isOther(): boolean {
    return this.pageData.useModel?.level1 === this.otherValueCode;
  }

  //#region Angular lifecycle hooks
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disableNativeInput']) {
      if (this.disableNativeInput === true) {
        this.enableAddressForNativeLanguage = !this.disableNativeInput;
      }
    }
  }

  ngOnInit(): void {
    if (isNullOrEmpty(this.printedAddressFormat)) {
      this.printedAddressFormat =
        this.envConfig?.format?.address?.printedAddressFormat ?? '';
    }

    if (isNullOrEmpty(this.maxAddressLevel)) {
      this.maxAddressLevel =
        this.envConfig?.format?.address?.maxAddressLevel ?? 4;
    }

    if (isNullOrEmpty(this.postcodeLevel)) {
      this.postcodeLevel = this.envConfig?.format?.address?.postcodeLevel;
    }

    // 取得 Level 1 下拉選單
    this.dropdownOptionsService
      .getCommonDropdownOptions(
        ADDRESS_LEVEL_TYPE.LEVEL1,
        undefined,
        false,
        true
      )
      .subscribe(
        NGUtils.genCommonObserverObj({
          next: (results) => {
            this.displayLevel1Options = this.globalEnableAddressForNative
              ? results.map((x) => ({
                  label: x.dropdown.languageEnglish ?? x.dropdown.name,
                  value: x.value,
                }))
              : results;
            this.level1Options = results;
            this.onLevel1Change(false);
            this.cd.detectChanges();
          },
        })
      );
  }

  ngDoCheck(): void {
    if (this.doCheckLock === false) {
      this.doCheckLock = true;
      const currentAddressModel: AddressViewModel =
        this.pageData.useModel ?? {};
      if (currentAddressModel.level5) {
        currentAddressModel.level5 = currentAddressModel.level5.trim();
      }
      if (currentAddressModel.native) {
        currentAddressModel.native = currentAddressModel.native.trim();
      }
      const currentAddressModelJson = JSON.stringify(currentAddressModel);
      if (currentAddressModelJson !== this.previousAddressModelJson) {
        if (!this.triggeredByUI) {
          this.logger.debug(
            `address model changed.`,
            currentAddressModelJson,
            this.previousAddressModelJson
          );
          this.writeValue(currentAddressModel);
          setTimeout(() => {
            this.onModelChange(this.pageData.useModel);
          }, 0);
          const previousAddressModel: AddressViewModel = JSON.parse(
            this.previousAddressModelJson
          );
          if (previousAddressModel.level1 !== currentAddressModel.level1) {
            this.onLevel1Change(false);
          } else if (
            previousAddressModel.level2 !== currentAddressModel.level2
          ) {
            this.onLevel2Change(false);
          } else if (
            previousAddressModel.level3 !== currentAddressModel.level3
          ) {
            this.onLevel3Change(false);
          } else if (
            previousAddressModel.level4 !== currentAddressModel.level4
          ) {
            this.onLevel4Change(false);
          } else if (
            previousAddressModel.postcode !== currentAddressModel.postcode
          ) {
            this.onPostcodeChange(false);
          } else {
          }
          if (previousAddressModel.level5 !== currentAddressModel.level5) {
            this.onLevel5Change(false);
          }
          if (previousAddressModel.native !== currentAddressModel.native) {
            this.onNativeAddrChange(false);
          }
          this.previousAddressModelJson = currentAddressModelJson;
        }
      }
      this.doCheckLock = false;
    }
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }
  //#endregion Angular lifecycle hooks

  //#region ControlValueAccessor 實作
  writeValue(obj: any): void {
    this.pageData.useModel = obj ?? {};
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onModelTouched = fn;
  }
  //#endregion ControlValueAccessor 實作

  //#region Event callback
  /**
   * Level 1 下拉選單 model change 事件
   *
   * @param {boolean} [fromUI=true] 是否為 UI 觸發
   * @memberof AddressSelectorComponent
   */
  @AutoLog()
  onLevel1Change(fromUI = true): void {
    this.triggeredByUI = fromUI;
    // 有顯示該層就去做處理
    if (this.checkIsLevelAvalaible(2)) {
      // 從 UI trigger 的要清除下一層資料
      if (fromUI) delete this.pageData.useModel.level2;
      // 有資料再去拿下層下拉選單
      if (isNullOrEmpty(this.pageData.useModel.level1) === false) {
        this.dropdownOptionsService
          .getCommonDropdownOptions(
            ADDRESS_LEVEL_TYPE.LEVEL2,
            this.pageData.useModel.level1,
            false,
            true
          )
          .subscribe(
            NGUtils.genCommonObserverObj({
              next: (results) => {
                this.displayLevel2Options = this.globalEnableAddressForNative
                  ? results.map((x) => ({
                      label: x.dropdown.languageEnglish ?? x.dropdown.name,
                      value: x.value,
                    }))
                  : results;
                this.level2Options = results;
                this.control?.updateValueAndValidity({
                  onlySelf: true,
                  emitEvent: false,
                });
                this.cd.detectChanges();
              },
            })
          );
      } else {
        this.level2Options = [];
      }
      this.onLevel2Change(fromUI);
    }

    if (this.enablePostcode) {
      // 如果 postcode 在該層
      if (this.postcodeLevel === AddressLevelEnum.level1) {
        this.disablePostcodeDDLFunc = () => !this.pageData.useModel.level1;
        // 從 UI trigger 的要清除 postcode
        if (fromUI) delete this.pageData.useModel.postcode;
        // 有資料再去拿 postcode 下拉選單
        if (isNullOrEmpty(this.pageData.useModel.level1) === false) {
          this.dropdownOptionsService
            .getPostcodeDropdownOptions(this.pageData.useModel.level1!, true)
            .subscribe(
              NGUtils.genCommonObserverObj({
                next: (results) => {
                  // 過濾掉重複
                  this.postcodeOptions = results.filter(
                    (x, idx, array) =>
                      array.findIndex((y) => y.value === x.value) === idx
                  );
                  this.control?.updateValueAndValidity({
                    onlySelf: true,
                    emitEvent: false,
                  });
                },
              })
            );
        } else {
          this.postcodeOptions = [];
        }
        this.onPostcodeChange(fromUI);
      }
    }

    if (fromUI) {
      this.onModelChange(this.pageData.useModel);
      this.triggeredByUI = false;
    }
    this.valueChange.emit({
      level: AddressLevelEnum.level1,
      value: this.pageData.useModel.level1,
    });
  }

  /**
   * Level 2 下拉選單 model change 事件
   *
   * @param {boolean} [fromUI=true]
   * @memberof AddressSelectorComponent
   */
  @AutoLog()
  onLevel2Change(fromUI = true): void {
    this.triggeredByUI = fromUI;
    // 有顯示該層就去做處理
    if (this.checkIsLevelAvalaible(3)) {
      if (fromUI) delete this.pageData.useModel.level3;
      // 有資料再去拿下層下拉選單
      if (isNullOrEmpty(this.pageData.useModel.level2) === false) {
        this.dropdownOptionsService
          .getCommonDropdownOptions(
            ADDRESS_LEVEL_TYPE.LEVEL3,
            this.pageData.useModel.level2,
            false,
            true
          )
          .subscribe(
            NGUtils.genCommonObserverObj({
              next: (results) => {
                this.displayLevel3Options = this.globalEnableAddressForNative
                  ? results.map((x) => ({
                      label: x.dropdown.languageEnglish ?? x.dropdown.name,
                      value: x.value,
                    }))
                  : results;
                this.level3Options = results;
                this.control?.updateValueAndValidity({
                  onlySelf: true,
                  emitEvent: false,
                });
                this.cd.detectChanges();
              },
            })
          );
      } else {
        this.level3Options = [];
      }
      this.onLevel3Change(fromUI);
    }

    if (this.enablePostcode) {
      // 如果 postcode 在該層
      if (this.postcodeLevel === AddressLevelEnum.level2) {
        this.disablePostcodeDDLFunc = () => !this.pageData.useModel.level2;
        // 從 UI trigger 的要清除下一層資料
        if (fromUI) delete this.pageData.useModel.postcode;
        // 有資料再去拿 postcode 下拉選單
        if (isNullOrEmpty(this.pageData.useModel.level2) === false) {
          this.dropdownOptionsService
            .getPostcodeDropdownOptions(this.pageData.useModel.level2!, true)
            .subscribe(
              NGUtils.genCommonObserverObj({
                next: (results) => {
                  // 過濾掉重複
                  this.postcodeOptions = results.filter(
                    (x, idx, array) =>
                      array.findIndex((y) => y.value === x.value) === idx
                  );
                  this.control?.updateValueAndValidity({
                    onlySelf: true,
                    emitEvent: false,
                  });
                },
              })
            );
        } else {
          this.postcodeOptions = [];
        }
        this.onPostcodeChange(fromUI);
      }
    }

    if (fromUI) {
      this.onModelChange(this.pageData.useModel);
      this.triggeredByUI = false;
    }
    this.valueChange.emit({
      level: AddressLevelEnum.level2,
      value: this.pageData.useModel.level2,
    });
  }

  /**
   * Level 3 下拉選單 model change 事件
   *
   * @param {boolean} [fromUI=true]
   * @memberof AddressSelectorComponent
   */
  @AutoLog()
  onLevel3Change(fromUI = true): void {
    this.triggeredByUI = fromUI;
    // 有顯示該層就去做處理
    if (this.checkIsLevelAvalaible(4)) {
      if (fromUI) delete this.pageData.useModel.level4;
      // 有資料再去拿下層下拉選單
      if (isNullOrEmpty(this.pageData.useModel.level3) === false) {
        this.dropdownOptionsService
          .getCommonDropdownOptions(
            ADDRESS_LEVEL_TYPE.LEVEL4,
            this.pageData.useModel.level3,
            false,
            true
          )
          .subscribe(
            NGUtils.genCommonObserverObj({
              next: (results) => {
                this.displayLevel4Options = this.globalEnableAddressForNative
                  ? results.map((x) => ({
                      label: x.dropdown.languageEnglish ?? x.dropdown.name,
                      value: x.value,
                    }))
                  : results;
                this.level4Options = results;
                this.control?.updateValueAndValidity({
                  onlySelf: true,
                  emitEvent: false,
                });
                this.cd.detectChanges();
              },
            })
          );
      } else {
        this.level4Options = [];
      }
      this.onLevel4Change(fromUI);
    }

    if (this.enablePostcode) {
      // 如果 postcode 在該層
      if (this.postcodeLevel === AddressLevelEnum.level3) {
        this.disablePostcodeDDLFunc = () => !this.pageData.useModel.level3;
        // 從 UI trigger 的要清除 postcode
        if (fromUI) delete this.pageData.useModel.postcode;
        // 有資料再去拿 postcode 下拉選單
        if (isNullOrEmpty(this.pageData.useModel.level3) === false) {
          this.dropdownOptionsService
            .getPostcodeDropdownOptions(this.pageData.useModel.level3!, true)
            .subscribe(
              NGUtils.genCommonObserverObj({
                next: (results) => {
                  // 過濾掉重複
                  this.postcodeOptions = results.filter(
                    (x, idx, array) =>
                      array.findIndex((y) => y.value === x.value) === idx
                  );
                  this.control?.updateValueAndValidity({
                    onlySelf: true,
                    emitEvent: false,
                  });
                },
              })
            );
        } else {
          this.postcodeOptions = [];
        }
        this.onPostcodeChange(fromUI);
      }
    }

    if (fromUI) {
      this.onModelChange(this.pageData.useModel);
      this.triggeredByUI = false;
    }
    this.valueChange.emit({
      level: AddressLevelEnum.level3,
      value: this.pageData.useModel.level3,
    });
  }

  /**
   * Level 4 下拉選單 model change 事件
   *
   * @param {boolean} [fromUI=true]
   * @memberof AddressSelectorComponent
   */
  @AutoLog()
  onLevel4Change(fromUI = true): void {
    this.triggeredByUI = fromUI;
    if (this.enablePostcode) {
      // 如果 postcode 在該層
      if (this.postcodeLevel === AddressLevelEnum.level4) {
        this.disablePostcodeDDLFunc = () => !this.pageData.useModel.level4;
        // 從 UI trigger 的要清除 postcode
        if (fromUI) delete this.pageData.useModel.postcode;
        // 有資料再去拿 postcode 下拉選單
        if (isNullOrEmpty(this.pageData.useModel.level4) === false) {
          this.dropdownOptionsService
            .getPostcodeDropdownOptions(this.pageData.useModel.level4!, true)
            .subscribe(
              NGUtils.genCommonObserverObj({
                next: (results) => {
                  // 過濾掉重複
                  this.postcodeOptions = results.filter(
                    (x, idx, array) =>
                      array.findIndex((y) => y.value === x.value) === idx
                  );
                  this.control?.updateValueAndValidity({
                    onlySelf: true,
                    emitEvent: false,
                  });
                },
              })
            );
        } else {
          this.postcodeOptions = [];
        }
        this.onPostcodeChange(fromUI);
      }
    }

    if (fromUI) {
      this.onModelChange(this.pageData.useModel);
      this.triggeredByUI = false;
    }
    this.valueChange.emit({
      level: AddressLevelEnum.level4,
      value: this.pageData.useModel.level4,
    });
  }

  /**
   * Level 5 輸入框 model change 事件
   *
   * @param {boolean} [fromUI=true]
   * @memberof AddressSelectorComponent
   */
  @AutoLog()
  onLevel5Change(fromUI = true): void {
    this.triggeredByUI = fromUI;
    if (fromUI) {
      this.onModelChange(this.pageData.useModel);

      if (this.pageData.useModel.level5?.length! > this.level5MaxLength) {
        setTimeout(() => {
          this.control?.setErrors({
            addressLevel5ExceededMaxLength: true,
          } as ErrObj);
        });
      }
      this.control?.updateValueAndValidity({
        onlySelf: true,
        emitEvent: false,
      });
      this.triggeredByUI = false;
    }
    this.valueChange.emit({
      level: AddressLevelEnum.level5,
      value: this.pageData.useModel.level5,
    });
  }

  /**
   *  Post code 輸入框 model change 事件
   *
   * @param {boolean} [fromUI=true]
   * @memberof AddressSelectorComponent
   */
  @AutoLog()
  onPostcodeChange(fromUI = true): void {
    this.triggeredByUI = fromUI;
    if (fromUI) {
      this.onModelChange(this.pageData.useModel);
      this.control?.updateValueAndValidity({
        onlySelf: true,
        emitEvent: false,
      });
      this.triggeredByUI = false;
    }
    this.valueChange.emit({
      level: AddressLevelEnum.postcode,
      value: this.pageData.useModel.postcode,
    });
  }

  @AutoLog()
  onNativeAddrChange(fromUI = true): void {
    this.triggeredByUI = fromUI;
    if (fromUI) {
      this.onModelChange(this.pageData.useModel);
      this.triggeredByUI = false;
    }
    this.valueChange.emit({
      level: AddressLevelEnum.native,
      value: this.pageData.useModel.native,
    });
  }
  //#endregion Event callback

  /**
   * 取得計算後的 column class
   *
   * @param {number} size
   * @return {*}  {string}
   * @memberof AddressSelectorComponent
   */
  getColSizeClass(size: number): string {
    if (this.layout > 4) {
      return `p-col-${this.layout}-${size}`;
    }
    const unit = Math.ceil(12 / this.layout);
    const portion = size * unit;
    return `p-col-${portion}`;
  }

  /**
   * 檢查是否該 level 是否可顯示
   *
   * @param {number} level
   * @return {*}  {boolean}
   * @memberof AddressSelectorComponent
   */
  checkIsLevelAvalaible(level: number): boolean {
    return level <= this.maxAddressLevel!;
  }

  /**
   * 檢查該 level 是否有檢核錯誤
   *
   * @param {string} errCode
   * @return {*}  {boolean}
   * @memberof AddressSelectorComponent
   */
  checkIsLevelInvalid(errCode: string): boolean {
    const errors = Object.keys(this.control?.errors ?? {});
    return (
      errors.includes(errCode) &&
      ((this.control?.dirty ?? false) || (this.control?.touched ?? false))
    );
  }

  /**
   * 取得唯讀顯示字串
   *
   * @return {*}  {(string | null)}
   * @memberof AddressSelectorComponent
   */
  getDisplayAddress(): string | null {
    const level1Label = this.isOther
      ? null
      : this.globalEnableAddressForNative
      ? formatOptionsForLocale(
          this.pageData.useModel.level1,
          this.level1Options,
          false,
          'en-us'
        )
      : formatOptions(this.pageData.useModel.level1, this.level1Options);
    const level2Label = this.isOther
      ? null
      : this.globalEnableAddressForNative
      ? formatOptionsForLocale(
          this.pageData.useModel.level2,
          this.level2Options,
          false,
          'en-us'
        )
      : formatOptions(this.pageData.useModel.level2, this.level2Options);
    const level3Label = this.isOther
      ? null
      : this.globalEnableAddressForNative
      ? formatOptionsForLocale(
          this.pageData.useModel.level3,
          this.level3Options,
          false,
          'en-us'
        )
      : formatOptions(this.pageData.useModel.level3, this.level3Options);
    const level4Label = this.isOther
      ? null
      : this.globalEnableAddressForNative
      ? formatOptionsForLocale(
          this.pageData.useModel.level4,
          this.level4Options,
          false,
          'en-us'
        )
      : formatOptions(this.pageData.useModel.level4, this.level4Options);
    const level5Label = this.pageData.useModel.level5;
    const postcodeLabel = this.enablePostcode
      ? this.isOther
        ? null
        : this.pageData.useModel.postcode
      : null;
    const nativeLabel = this.enableAddressForNativeLanguage
      ? this.pageData.useModel.native
      : null;

    let displayAddress = this.printedAddressFormat
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL1 + '}',
        isNullOrEmpty(level1Label) ? '' : `${level1Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL2 + '}',
        isNullOrEmpty(level2Label) ? '' : `${level2Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL3 + '}',
        isNullOrEmpty(level3Label) ? '' : `${level3Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL4 + '}',
        isNullOrEmpty(level4Label) ? '' : `${level4Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL5 + '}',
        isNullOrEmpty(level5Label) ? '' : `${level5Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.POSTCODE + '}',
        isNullOrEmpty(postcodeLabel) ? '' : `${postcodeLabel}`
      )
      .trim();
    displayAddress = this.prettyAddressFormat(displayAddress);
    if (!isNullOrEmpty(nativeLabel) && this.enableAddressForNativeLanguage) {
      const newLineChar = isNullOrEmpty(displayAddress) ? '' : '\n';
      displayAddress = `${displayAddress}${newLineChar}${nativeLabel}`;
    }

    displayAddress = isNullOrEmpty(displayAddress)
      ? this.replaceNullWith
      : displayAddress;

    return displayAddress;
  }

  /**
   * 取得唯讀顯示字串(Native Address)
   *
   * @return {*}  {(string | null)}
   * @memberof AddressSelectorComponent
   */
  getDisplayNativeAddress(): string | null {
    const locale = this.envConfig?.locale.toLowerCase();
    const level1Label = this.isOther
      ? null
      : formatOptionsForLocale(
          this.pageData.useModel.level1,
          this.level1Options,
          false,
          locale
        );
    const level2Label = this.isOther
      ? null
      : formatOptionsForLocale(
          this.pageData.useModel.level2,
          this.level2Options,
          false,
          locale
        );
    const level3Label = this.isOther
      ? null
      : formatOptionsForLocale(
          this.pageData.useModel.level3,
          this.level3Options,
          false,
          locale
        );
    const level4Label = this.isOther
      ? null
      : formatOptionsForLocale(
          this.pageData.useModel.level4,
          this.level4Options,
          false,
          locale
        );
    const level5Label = null;
    const postcodeLabel = this.enablePostcode
      ? this.isOther
        ? null
        : this.pageData.useModel.postcode
      : null;
    const nativeLabel = this.enableAddressForNativeLanguage
      ? this.pageData.useModel.native
      : null;

    let displayAddress = this.printedAddressFormat
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL1 + '}',
        isNullOrEmpty(level1Label) ? '' : `${level1Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL2 + '}',
        isNullOrEmpty(level2Label) ? '' : `${level2Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL3 + '}',
        isNullOrEmpty(level3Label) ? '' : `${level3Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL4 + '}',
        isNullOrEmpty(level4Label) ? '' : `${level4Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL5 + '}',
        isNullOrEmpty(level5Label) ? '' : `${level5Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.POSTCODE + '}',
        isNullOrEmpty(postcodeLabel) ? '' : `${postcodeLabel}`
      )
      .trim();
    displayAddress = this.prettyAddressFormat(displayAddress);
    if (!isNullOrEmpty(nativeLabel) && this.enableAddressForNativeLanguage) {
      displayAddress = `${nativeLabel}, ${displayAddress}`;
    }

    displayAddress = isNullOrEmpty(displayAddress)
      ? this.replaceNullWith
      : displayAddress;

    return displayAddress;
  }

  /**
   * 取得唯讀顯示字串(補上鄉鎮市區)
   *
   * @return {*}  {(string | null)}
   * @memberof AddressSelectorComponent
   */
  getDisplayAddressWithLabel(): string | null {
    const level1Label = this.isOther
      ? null
      : this.globalEnableAddressForNative
      ? formatOptionsForLocale(
          this.pageData.useModel.level1,
          this.level1Options,
          false,
          'en-us'
        )
      : formatOptions(this.pageData.useModel.level1, this.level1Options);
    const level2Label = this.isOther
      ? null
      : this.globalEnableAddressForNative
      ? formatOptionsForLocale(
          this.pageData.useModel.level2,
          this.level2Options,
          false,
          'en-us'
        )
      : formatOptions(this.pageData.useModel.level2, this.level2Options);
    const level3Label = this.isOther
      ? null
      : this.globalEnableAddressForNative
      ? formatOptionsForLocale(
          this.pageData.useModel.level3,
          this.level3Options,
          false,
          'en-us'
        )
      : formatOptions(this.pageData.useModel.level3, this.level3Options);
    const level4Label = this.isOther
      ? null
      : this.globalEnableAddressForNative
      ? formatOptionsForLocale(
          this.pageData.useModel.level4,
          this.level4Options,
          false,
          'en-us'
        )
      : formatOptions(this.pageData.useModel.level4, this.level4Options);
    const level5Label = this.pageData.useModel.level5;
    const postcodeLabel = this.enablePostcode
      ? this.isOther
        ? null
        : this.pageData.useModel.postcode
      : null;

    let displayAddress = this.printedAddressFormat
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL1 + '}',
        isNullOrEmpty(level1Label) ? '' : `${level1Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL2 + '}',
        isNullOrEmpty(level2Label) ? '' : `Kahn ${level2Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL3 + '}',
        isNullOrEmpty(level3Label) ? '' : `Sangkat ${level3Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL4 + '}',
        isNullOrEmpty(level4Label) ? '' : `Village ${level4Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL5 + '}',
        isNullOrEmpty(level5Label) ? '' : `${level5Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.POSTCODE + '}',
        isNullOrEmpty(postcodeLabel) ? '' : `${postcodeLabel}`
      )
      .trim();
    displayAddress = this.prettyAddressFormat(displayAddress);
    displayAddress = isNullOrEmpty(displayAddress)
      ? this.replaceNullWith
      : displayAddress;

    return displayAddress;
  }

  /**
   * 取得唯讀顯示字串(Native Language下拉選單)
   *
   * @return {*}  {(string | null)}
   * @memberof AddressSelectorComponent
   */
  getDisplayNativeAddressOptions(): string | null {
    const locale = this.envConfig?.locale.toLowerCase();
    const level1Label = this.isOther
      ? null
      : formatOptionsForLocale(
          this.pageData.useModel.level1,
          this.level1Options,
          false,
          locale
        );
    const level2Label = this.isOther
      ? null
      : formatOptionsForLocale(
          this.pageData.useModel.level2,
          this.level2Options,
          false,
          locale
        );
    const level3Label = this.isOther
      ? null
      : formatOptionsForLocale(
          this.pageData.useModel.level3,
          this.level3Options,
          false,
          locale
        );
    const level4Label = this.isOther
      ? null
      : formatOptionsForLocale(
          this.pageData.useModel.level4,
          this.level4Options,
          false,
          locale
        );
    const level5Label = null;
    const postcodeLabel = this.enablePostcode
      ? this.isOther
        ? null
        : this.pageData.useModel.postcode
      : null;

    let displayAddress = this.printedAddressFormat
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL1 + '}',
        isNullOrEmpty(level1Label) ? '' : `${level1Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL2 + '}',
        isNullOrEmpty(level2Label) ? '' : `${level2Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL3 + '}',
        isNullOrEmpty(level3Label) ? '' : `${level3Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL4 + '}',
        isNullOrEmpty(level4Label) ? '' : `${level4Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.LEVEL5 + '}',
        isNullOrEmpty(level5Label) ? '' : `${level5Label},`
      )
      .replace(
        '${' + ADDRESS_LEVEL_TYPE.POSTCODE + '}',
        isNullOrEmpty(postcodeLabel) ? '' : `${postcodeLabel}`
      )
      .trim();
    displayAddress = this.prettyAddressFormat(displayAddress);
    displayAddress = isNullOrEmpty(displayAddress)
      ? this.replaceNullWith
      : displayAddress;

    return displayAddress;
  }

  private prettyAddressFormat(rawAddress: string): string {
    let prettyAddress = rawAddress.trim();
    if (/\,\s{0,}\,/g.test(prettyAddress)) {
      prettyAddress = prettyAddress.replace(/\,\s{0,}\,/g, ',').trim();
    }
    if (rawAddress.startsWith(',')) {
      prettyAddress = prettyAddress.replace(',', '').trim();
    }
    prettyAddress = this.removeLastIndexOfChar(prettyAddress, ',', '').trim();

    return prettyAddress;
  }

  private removeLastIndexOfChar(
    str: string,
    char: string,
    replaceChar: string
  ): string {
    let finalStr = str;
    const idx = str.lastIndexOf(char);
    if (idx === str.length - 1) {
      finalStr = str.substring(0, str.length - 1).concat(replaceChar);
    }

    return finalStr;
  }
}

/**
 * 地址格式檢核
 *
 * @export
 * @class AddressValidators
 */
export class AddressValidators {
  /**
   * 地址必填檢核
   *
   * @static
   * @param {number} [maxDropdownLevel]
   * @return {*}  {(ValidatorFn | null)}
   * @memberof AddressValidators
   */
  static addressRequired(
    maxDropdownLevel?: number,
    addressSelector?: AddressSelectorComponent,
    model?: { useModel: AddressViewModel }
  ): ValidatorFn | null {
    const maxLevel =
      maxDropdownLevel ??
      AppEnvService.staticEnvConfig().format?.address?.maxAddressLevel ??
      4;
    const enablePostcode =
      AppEnvService.staticEnvConfig().format?.address?.enablePostcode ?? false;

    return (ctrl: AbstractControl): ValidationErrors | null => {
      const val: AddressViewModel = model?.useModel
        ? model.useModel
        : ctrl.value ?? {};
      const errObj = {} as ErrObj;
      if (
        isNullOrEmpty(val.level1) &&
        (addressSelector?.level1Options ?? []).length > 0
      ) {
        errObj['addressLevel1Requied'] = true;
      }
      if (
        2 <= maxLevel &&
        isNullOrEmpty(val.level2) &&
        (addressSelector?.level2Options ?? []).length > 0
      ) {
        errObj['addressLevel2Requied'] = true;
      }

      if (
        3 <= maxLevel &&
        isNullOrEmpty(val.level3) &&
        (addressSelector?.level3Options ?? []).length > 0
      ) {
        errObj['addressLevel3Requied'] = true;
      }

      if (
        4 <= maxLevel &&
        isNullOrEmpty(val.level4) &&
        (addressSelector?.level4Options ?? []).length > 0
      ) {
        errObj['addressLevel4Requied'] = true;
      }

      const level5Value = val.level5?.trim() ?? '';
      if (isNullOrEmpty(level5Value)) {
        errObj['addressLevel5Requied'] = true;
      } else {
        if (level5Value.length > 2100) {
          errObj['addressLevel5ExceededMaxLength'] = true;
        }
      }

      if (enablePostcode) {
        if (
          isNullOrEmpty(val.postcode) &&
          (addressSelector?.postcodeOptions ?? []).length > 0
        ) {
          errObj['addressPostcodeRequied'] = true;
        }
      }

      if (errObj['required']) {
        const { required, ...rest } = errObj;
        return rest;
      }
      return errObj;
    };
  }

  /**
   * Address for native languarge 欄位必填檢核
   *
   * @static
   * @return {*}  {(ValidatorFn | null)}
   * @memberof AddressValidators
   */
  static nativeAddressRequired(): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      const val: AddressViewModel = ctrl.value ?? {};
      const errObj = {} as ErrObj;

      if (isNullOrEmpty(val.native?.trim())) {
        errObj['addressNativeRequied'] = true;
      }

      return errObj;
    };
  }

  /**
   * 地址必填檢核(submit 觸發)
   *
   * @static
   * @param {number} [maxDropdownLevel]
   * @param {AddressSelectorComponent} [addressSelector]
   * @return {*}  {(ValidatorFn | null)}
   * @memberof AddressValidators
   */
  static addressRequiredOnSubmit(
    maxDropdownLevel?: number,
    addressSelector?: AddressSelectorComponent,
    model?: { useModel: AddressViewModel }
  ): ValidatorFn | null {
    const maxLevel =
      maxDropdownLevel ??
      AppEnvService.staticEnvConfig().format?.address.maxAddressLevel ??
      4;

    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (isNullOrEmpty(ctrl)) return null;
      const formEx = getFormGroupEx(ctrl);
      return formEx?.submitted
        ? AddressValidators.addressRequired(
            maxLevel,
            addressSelector,
            model
          )!.call(this, ctrl)
        : null;
    };
  }

  /**
   * Address for native languarge 欄位必填檢核(submit 觸發)
   *
   * @static
   * @return {*}  {(ValidatorFn | null)}
   * @memberof AddressValidators
   */
  static nativeAddressRequiredOnSubmit(): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (isNullOrEmpty(ctrl)) return null;
      const formEx = getFormGroupEx(ctrl);
      return formEx?.submitted
        ? AddressValidators.nativeAddressRequired()!.call(this, ctrl)
        : null;
    };
  }
}

/**
 * 地址元件資料結構
 *
 * @export
 * @interface AddressViewModel
 */
export interface AddressViewModel {
  level1?: string;
  level2?: string;
  level3?: string;
  level4?: string;
  level5?: string;
  postcode?: string;
  native?: string;
}
interface ErrObj {
  addressLevel1Requied: boolean;
  addressLevel2Requied: boolean;
  addressLevel3Requied: boolean;
  addressLevel4Requied: boolean;
  addressLevel5Requied: boolean;
  addressPostcodeRequied: boolean;
  addressNativeRequied: boolean;
  addressLevel5ExceededMaxLength: boolean;
  required: boolean;
}
